import { Link } from 'react-router-dom';
import { AlertTriangle, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Valid US states
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

// Validate US zip code format (5 digits or 5+4)
const isValidZip = (zip) => {
  if (!zip) return false;
  const zipStr = String(zip).trim();
  return /^\d{5}(-\d{4})?$/.test(zipStr);
};

// Validate US state
const isValidState = (state) => {
  if (!state) return false;
  return US_STATES.includes(state.toUpperCase());
};

// Check if address is valid for shipping
export const validateAddress = (userProfile) => {
  if (!userProfile) return { valid: false, issues: ['Not logged in'] };
  
  const issues = [];
  const isInternational = userProfile.country && userProfile.country !== 'US';
  
  // Check required fields for all users
  if (!userProfile.address || userProfile.address.trim() === '') {
    issues.push('Street address is required');
  }
  if (!userProfile.city || userProfile.city.trim() === '') {
    issues.push('City is required');
  }
  // State just needs to exist - backend already validated it at registration
  if (!userProfile.state || userProfile.state.trim() === '') {
    issues.push('State is required');
  }
  if (!userProfile.zip_code || !isValidZip(userProfile.zip_code)) {
    issues.push('Valid US zip code is required (5 digits)');
  }
  
  // International users need suite/unit number (forwarding service requirement)
  if (isInternational) {
    // Check if admin has verified their forwarding address
    if (userProfile.forwarding_verified) {
      // Admin verified - skip additional checks
      return { valid: issues.length === 0, issues, isInternational, forwardingVerified: true };
    }
    
    if (!userProfile.apt_unit || userProfile.apt_unit.trim() === '') {
      issues.push('Suite/Unit number is required for international customers (your forwarding service suite #)');
    }
  }
  
  return { 
    valid: issues.length === 0, 
    issues, 
    isInternational,
    forwardingVerified: userProfile.forwarding_verified || false
  };
};

export default function AddressValidationBanner() {
  const { userProfile, user, loading } = useAuth();
  
  // Don't show while loading, for admins, or unauthenticated users
  if (loading || !user || user.is_admin) return null;
  
  // Don't show until userProfile is fully loaded
  if (!userProfile) return null;
  
  const validation = validateAddress(userProfile);
  
  // Address is valid - no banner needed
  if (validation.valid) return null;
  
  // Different messaging for international vs domestic users
  const isInternational = validation.isInternational;
  
  return (
    <div 
      className={`fixed top-16 left-0 right-0 z-40 px-3 py-1.5 flex items-center justify-between gap-2 ${
        isInternational 
          ? 'bg-yellow-500 text-black' 
          : 'bg-orange-500 text-black'
      }`}
      data-testid="address-validation-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="font-mono text-[10px] sm:text-xs truncate">
          {isInternational 
            ? 'US forwarding address + suite required' 
            : 'Complete shipping address required'
          }
        </span>
      </div>
      <Link 
        to="/dashboard?edit=address" 
        className="font-mono text-[10px] sm:text-xs font-bold bg-black/20 px-2 py-1 hover:bg-black/30 flex-shrink-0"
      >
        FIX NOW
      </Link>
    </div>
  );
}
